import { safeGet } from '../../../utils/safeGet'
import { get as _get, post as _post } from '../../../utils/fetcher'
import { INIT_DEFINITION_SETTINGS, SCORING_DEFINITION_TYPES } from '../../../settings/scoringProfilesSettings'
import { mapScoringDefinitionforModel, getMaxTotalDriversWeight } from '../../../utils/drivers'

export default {
  namespaced: true,

  state: {
    selectedWeightType: 1,
    defaultSelectedWeightType: 1,
    sequenceWeightsDecay: 0.5,
    defaultSequenceWeightsDecay: 0.5,
    driversIndicators: {},
    driversFamiliesIndicators: {},
    selectedModelId: null,
    selectedModelName: null,
    defaultSelectedModelId: null,
    defaultModelDrivers: [],
    sortedModelDrivers: [],
    unsortedModelDrivers: [],
    sortedModelWeights: [],
    scoringDefinitionSettings: INIT_DEFINITION_SETTINGS(),
    defaultScoringDefinitionSettings: INIT_DEFINITION_SETTINGS(),
    scoringProfileDefinitionSelectValue: null,
  },

  mutations: {
    setScoringProfileDefinitionSelectValue(state, data) {
      state.scoringProfileDefinitionSelectValue = data
    },

    setSortedModelWeights(state, data) {
      state.sortedModelWeights = data
    },

    setSelectedModelId(state, data) {
      state.selectedModelId = data
    },

    setSelectedModelName(state, data) {
      state.selectedModelName = data
    },

    setDefaultSelectedModelId(state, data) {
      state.defaultSelectedModelId = data
    },

    setSortedModelDrivers(state, data) {
      state.sortedModelDrivers = data
    },

    setUnsortedModelDrivers(state, data) {
      state.unsortedModelDrivers = data
    },

    setDefaultModelDrivers(state, data) {
      state.defaultModelDrivers = data
    },

    setDriversIndicators(state, data) {
      state.driversIndicators = data
    },

    setDriversFamiliesIndicators(state, data) {
      state.driversFamiliesIndicators = data
    },

    setScoringDefinitionSettings(state, data) {
      state.scoringDefinitionSettings = data
    },

    setDefaultScoringDefinitionSettings(state, data) {
      state.defaultScoringDefinitionSettings = data
    },

    setSelectedWeightType(state, payload) {
      state.selectedWeightType = payload
    },

    setDefaultSelectedWeightType(state, payload) {
      state.defaultSelectedWeightType = payload
    },

    setSequenceWeightsDecay(state, payload) {
      state.sequenceWeightsDecay = payload
    },

    setDefaultSequenceWeightsDecay(state, payload) {
      state.defaultSequenceWeightsDecay = payload
    },

    setDriversList(state, data) {
      state.scoringDefinitionSettings[state.selectedWeightType].list = data
    },
  },

  actions: {
    setSelectedModelId({ commit }, data) {
      commit('setSelectedModelId', data)
    },

    setScoringProfileDefinitionSelectValue({ commit }, data) {
      commit('setScoringProfileDefinitionSelectValue', data)
    },

    setSelectedModelName({ commit }, data) {
      commit('setSelectedModelName', data)
    },

    setDefaultSelectedModelId({ commit }, data) {
      commit('setDefaultSelectedModelId', data)
    },

    setSortedModelDrivers({ commit }, data) {
      commit('setSortedModelDrivers', data)
    },

    setUnsortedModelDrivers({ commit }, data) {
      commit('setUnsortedModelDrivers', data)
    },

    resetSelectedModel({ commit }) {
      commit('setSelectedModelId', null)
      commit('setSelectedModelName', null)
      commit('setSortedModelDrivers', [])
      commit('setUnsortedModelDrivers', [])
    },

    setScoringDefinitionSettings({ commit }, data) {
      commit('setScoringDefinitionSettings', data)
    },

    setDefaultScoringDefinitionSettings({ commit }, data) {
      commit('setDefaultScoringDefinitionSettings', data)
    },

    setSelectedWeightType({ commit }, data) {
      commit('setSelectedWeightType', data)
    },

    setDefaultSelectedWeightType({ commit }, data) {
      commit('setDefaultSelectedWeightType', data)
    },

    setSequenceWeightsDecay({ commit }, data) {
      commit('setSequenceWeightsDecay', data)
    },

    setDefaultSequenceWeightsDecay({ commit }, data) {
      commit('setDefaultSequenceWeightsDecay', data)
    },

    setDriversList({ commit }, data) {
      commit('setDriversList', data)
    },

    setSortedModelDriversFromAPI({ commit, getters, rootGetters }, selectedOrder) {
      const models = rootGetters['scoringProfilesModelsList/getModelsListToAssociate']
      const selectedModelId = getters.getSelectedModelId
      const selectedModel = models.filter((el) => el.id === selectedModelId)[0] || null
      let result = { driversList: [], sortedWeights: [] }
      let sortedList = []

      if (selectedModel) {
        result = mapScoringDefinitionforModel(selectedModel.scoringDefinition)
        result.driversList.forEach((el, i) => {
          sortedList.push({ driver: selectedOrder[i] || el.driver, weight: el.weight })
        })
      }

      commit('setUnsortedModelDrivers', result.driversList)
      commit('setSortedModelDrivers', sortedList)
      commit('setSortedModelWeights', result.sortedWeights)
      commit('setDefaultModelDrivers', sortedList)
    },

    loadDriversIndicators({ commit }) {
      return new Promise((resolve, reject) => {
        _get('/api/scoringProfiles/indicators')
          .then((resp) => {
            if (!resp || resp.drivers.length === 0) return

            const result = resp.drivers.reduce((accumulator, reducer) => {
              const key = reducer.category
              const value = reducer.code

              accumulator['indicators'] = accumulator['indicators'] || {}
              accumulator.indicators[key] = accumulator.indicators[key] || []

              accumulator['indicatorsFamilies'] = accumulator['indicatorsFamilies'] || {}

              accumulator.indicators[key].push(value)

              if (Array.isArray(reducer.driverFamilies) && reducer.driverFamilies.length > 0) {
                reducer.driverFamilies.forEach((j) => {
                  if (!(j in accumulator.indicatorsFamilies)) {
                    accumulator.indicatorsFamilies[j] = accumulator.indicatorsFamilies[j] || []
                  }
                  accumulator.indicatorsFamilies[j].push(value)
                })
              }

              return accumulator
            }, Object.create(null))

            commit('setDriversIndicators', result.indicators)
            commit('setDriversFamiliesIndicators', result.indicatorsFamilies)

            resolve(result)
          })
          .catch((err) => reject(err))
      })
    },

    // The authoring page holds its filters in the catalog wire format, so they go to the API as-is:
    // the backend picks the ESMA target-market keys out of the collection and ignores the rest.
    suggestedSorting({ getters, rootGetters }) {
      const payload = {
        scoringProfileModelId: getters.getSelectedModelId,
        filters: rootGetters['scoringProfilesFilters/filtersPayload'],
      }

      return new Promise((resolve, reject) => {
        _post('/api/scoringProfileModels/recommendedDriversOrder', payload)
          .then((resp) => {
            resolve(resp)
          })
          .catch((err) => {
            reject(err)
          })
      })
    },
  },

  getters: {
    getSortedModelWeights: (state) => {
      return safeGet(() => state.sortedModelWeights, [])
    },

    getScoringProfileDefinitionSelectValue: (state) => {
      return safeGet(() => state.scoringProfileDefinitionSelectValue, null)
    },

    getSelectedModelId: (state) => {
      return safeGet(() => state.selectedModelId, null)
    },

    getSelectedModelName: (state) => {
      return safeGet(() => state.selectedModelName, null)
    },

    getUnsortedModelDrivers: (state) => {
      return safeGet(() => state.unsortedModelDrivers, [])
    },

    getSortedModelDrivers: (state) => {
      return safeGet(() => state.sortedModelDrivers, [])
    },

    getDriversIndicators: (state) => {
      return safeGet(() => state.driversIndicators, [])
    },

    getDriversFamiliesIndicators: (state) => {
      return safeGet(() => state.driversFamiliesIndicators, [])
    },

    getSelectedWeightType: (state) => {
      return safeGet(() => state.selectedWeightType, 1)
    },

    getDefaultSelectedWeightType: (state) => {
      return safeGet(() => state.defaultSelectedWeightType, 1)
    },

    getSequenceWeightsDecay: (state) => {
      return safeGet(() => state.sequenceWeightsDecay, 0.5)
    },

    getDefaultSequenceWeightsDecay: (state) => {
      return safeGet(() => state.defaultSequenceWeightsDecay, 0.5)
    },

    getScoringDefinitionSettings: (state) => {
      return safeGet(() => state.scoringDefinitionSettings, INIT_DEFINITION_SETTINGS())
    },

    getDefaultScoringDefinitionSettings: (state) => {
      return safeGet(() => state.defaultScoringDefinitionSettings, INIT_DEFINITION_SETTINGS())
    },

    getSelectedScoringDefinition: (state, getters) => {
      return safeGet(() => state.scoringDefinitionSettings[getters.getSelectedWeightType], null)
    },

    getMaxTotalWeight: (state, getters) => {
      const list = getters.getSelectedScoringDefinition.list
      return getMaxTotalDriversWeight(list)
    },

    getDriversIndicatorsCategories: (state) => {
      return safeGet(() => Object.keys(state.driversIndicators), [])
    },

    getDriversWeightsGreaterThanZero: (state, getters) => {
      const selectedWeightType = getters.getSelectedWeightType
      const scoringDefinitionSettings = getters.getScoringDefinitionSettings
      const driversList = scoringDefinitionSettings[selectedWeightType].list
      const isCustom = selectedWeightType === SCORING_DEFINITION_TYPES.custom
      const condition = isCustom ? driversList.length > 0 && driversList.every((item) => item.weight > 0) : true
      return safeGet(() => condition, false)
    },

    getScoringDefinitionDiff: (state, getters) => {
      const diffScoringDefinition =
        JSON.stringify(getters.getScoringDefinitionSettings) !==
        JSON.stringify(getters.getDefaultScoringDefinitionSettings)
      const diffSelectedWeightType = getters.getSelectedWeightType !== getters.getDefaultSelectedWeightType
      const diffSequenceWeightsDecay = getters.getSequenceWeightsDecay !== getters.getDefaultSequenceWeightsDecay

      return diffScoringDefinition || diffSelectedWeightType || diffSequenceWeightsDecay
    },

    getScoringDefinitionToSendToAPI: (state, getters) => {
      const scoringDefinitionSettings = getters.getScoringDefinitionSettings

      const findByKey = (object, value) => Object.keys(object).find((key) => object[key].config === value)
      const geometricKey = findByKey(scoringDefinitionSettings, SCORING_DEFINITION_TYPES.geometric)
      const customKey = findByKey(scoringDefinitionSettings, SCORING_DEFINITION_TYPES.custom)
      const linearKey = findByKey(scoringDefinitionSettings, SCORING_DEFINITION_TYPES.linear)

      const geometricDrivers = {
        drivers: scoringDefinitionSettings[geometricKey].list.map((item) => item.driver || item.family),
        sequenceWeightsDecay: getters.getSequenceWeightsDecay,
      }

      const mapList = (list) =>
        list.map((item) => {
          const key = 'family' in item ? 'family' : 'driver'
          return {
            driver: item[key],
            weight: item.weight,
          }
        })

      return {
        weightType: getters.getSelectedWeightType,
        geometricDrivers: geometricDrivers,
        customDrivers: mapList(scoringDefinitionSettings[customKey].list),
        linearDrivers: mapList(scoringDefinitionSettings[linearKey].list),
      }
    },

    getSortedModelDriversToSendToAPI: (state, getters) => {
      const sortedModelDrivers = getters.getSortedModelDrivers
      const list = sortedModelDrivers.map((el) => el.driver)

      return list
    },
  },
}
